import './styles/app.css';
import './styles/global.scss';
import './i18n';

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { initErrorReporting } from './lib/error-reporting';
import { logError } from './lib/logger';
import { AppProviders } from './providers';
import { AppRouter } from './routes';
import { installAuthBridge } from './stores/useAuthStore';

// http 層要拿得到 access token、401 時要換得到新的——啟動時把 store 接上去
installAuthBridge();

/**
 * 把網頁字型接上。
 *
 * `index.html` 那個 `<link>` 刻意標成 `media="print"`——瀏覽器照樣用高優先度去抓，
 * 但**不擋首次繪製**。那份 CSS 有 485 KB／426 條 @font-face（中文字型的子集），
 * 在 Slow 4G 上要 2.2 秒，而它原本是 render-blocking：首屏就押在那裡等。
 *
 * 這裡把 media 改回 `all`。文字在那之前用系統字型畫出來，字型到了再換
 * （URL 裡已經有 `display=swap`）。
 *
 * 為什麼不寫成 `<link onload=...>`：那是 inline 的事件處理器，而 CSP 是
 * `script-src 'self'`——不會執行。
 */
document.getElementById('webfont-css')?.setAttribute('media', 'all');

/**
 * 錯誤追蹤先接起來，才收得到掛載階段的錯誤。
 *
 * 不 await：它只是一個附加功能，不該卡住畫面出現的時間。沒設定 DSN 時它會立刻返回。
 */
void initErrorReporting();

const container = document.getElementById('root');

if (!container) {
  logError('找不到 #root，前端無法掛載');
} else {
  /**
   * `createRoot` 不是 `hydrateRoot`——即使 `#root` 裡已經有預渲染的內容。
   *
   * 這是刻意的。預渲染走的是 `entry-server.tsx`（直接渲染頁面元件，因為
   * `RootGate` 在伺服器端沒有 session 可問、只會渲染出空白），而 client 走的是
   * `AppRouter`。兩邊的樹不保證位元組相同，hydrate 會炸出 mismatch。
   *
   * 代價是 React 掛載時把預渲染的內容換掉一次。那不影響爬蟲（它們看的是 HTML），
   * 也不影響 LCP（首次繪製發生在換掉之前）。換來的是零 mismatch 風險——
   * 對一個「壞了不會有錯誤訊息、只會靜靜地變回空白」的機制，這個取捨值得。
   */
  createRoot(container).render(
    <StrictMode>
      <AppProviders>
        <AppRouter />
      </AppProviders>
    </StrictMode>,
  );
}
